import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRef = (params.get("ref") || "").toUpperCase();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", phone: "", role: "patient", referral_code: initialRef });
  const [busy, setBusy] = useState(false);
  const [refInfo, setRefInfo] = useState(null);
  const [refError, setRefError] = useState("");

  useEffect(() => {
    const code = (form.referral_code || "").toUpperCase().trim();
    if (!code || code.length < 4) { setRefInfo(null); setRefError(""); return; }
    let cancelled = false;
    api.get(`/referrals/lookup/${encodeURIComponent(code)}`)
      .then((r) => { if (!cancelled) { setRefInfo(r.data); setRefError(""); } })
      .catch((e) => { if (!cancelled) { setRefInfo(null); setRefError(apiError(e, "Invalid code")); } });
    return () => { cancelled = true; };
  }, [form.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, referral_code: (form.referral_code || "").trim() || undefined };
      const u = await register(payload);
      toast.success("Account created!");
      nav(u.role === "patient" ? "/patient" : u.role === "lab_admin" ? "/lab" : "/admin");
    } catch (err) {
      toast.error(apiError(err, "Registration failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-ink text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center">
          <img src="/efgh-logo-white.png" alt="EFGH" className="h-12" />
        </Link>
        <div>
          <h2 className="font-heading font-bold text-4xl leading-tight">Get your first<br />smart report free.</h2>
          <p className="mt-4 text-white/60 max-w-md">Upload any PDF or image lab report and see it transformed into a beautiful, easy-to-read health snapshot.</p>
          {refInfo && (
            <div className="mt-6 p-4 rounded-xl bg-white/10 border border-white/20 text-sm max-w-md">
              <div className="flex items-center gap-2 font-heading font-semibold"><Gift className="w-4 h-4 text-efgh-yellow" /> Referred by {refInfo.referrer_first_name}</div>
              <div className="mt-1 text-white/70 text-xs">{refInfo.description}</div>
            </div>
          )}
        </div>
        <div className="text-white/40 text-xs">© 2026 EFGH Health Technologies</div>
      </div>
      <div className="p-4 sm:p-8 lg:p-12 flex flex-col items-center justify-center">
        <Link to="/" className="lg:hidden mb-6 flex items-center" data-testid="register-mobile-logo">
          <img src="/efgh-logo.png" alt="EFGH" className="h-11" />
        </Link>
        <Card className="w-full max-w-md p-6 sm:p-8 rounded-2xl border-border">
          <h1 className="font-heading font-bold text-2xl text-ink">Create your account</h1>
          <p className="mt-1 text-sm text-ink/60">Start understanding your reports better</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>Full name</Label><Input data-testid="register-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required /></div>
            <div><Label>Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input data-testid="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} /></div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-efgh-orange" /> Referral code <span className="text-ink/40 text-xs font-normal">(optional)</span></Label>
              <Input data-testid="register-referral" value={form.referral_code || ""} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })} placeholder="e.g. ANJALIA23F" maxLength={32} />
              {refInfo && <div className="mt-1 text-xs text-efgh-green" data-testid="referral-valid">✓ Referred by {refInfo.referrer_first_name} — you get {refInfo.referee_reward?.percent ? `${refInfo.referee_reward.percent}% off` : ""}{refInfo.referee_reward?.flat ? ` ₹${refInfo.referee_reward.flat} off` : ""}</div>}
              {refError && !refInfo && form.referral_code && <div className="mt-1 text-xs text-danger" data-testid="referral-invalid">{refError}</div>}
            </div>
            <Button data-testid="register-submit" type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-dark text-white">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-center text-ink/60">
            Already have an account? <Link to="/login" className="text-brand font-medium">Sign in</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
